import {getRefreshToken, saveTokens} from '../utils/authStorage';

let cachedConfig: CognitoConfig | null = null;

export interface CognitoConfig {
  region: string;
  userPoolId: string;
  clientId: string;
  domain: string;
}

async function fetchCognitoConfigFromExports(): Promise<CognitoConfig> {
  // Get values from environment or infer from CloudFormation exports
  const region = import.meta.env.VITE_AWS_REGION || 'us-east-1';
  const environment = import.meta.env.VITE_ENVIRONMENT || 'dev';
  const stage = import.meta.env.VITE_STAGE || environment;

  try {
    // Fetch from CloudFormation (requires backend)
    // OR use environment variables that were set during deployment
    // ALWAYS use dev Cognito for dev environment regardless of stage
    const cognitoEnvironment = environment === 'dev' ? 'dev' : stage;
    const cognitoExportPrefix = `cedi-${cognitoEnvironment}`;

    const userPoolId =
      import.meta.env.VITE_COGNITO_USER_POOL_ID ||
      sessionStorage.getItem(`${cognitoExportPrefix}-userPoolId`) ||
      '';
    const clientId =
      import.meta.env.VITE_COGNITO_CLIENT_ID ||
      sessionStorage.getItem(`${cognitoExportPrefix}-clientId`) ||
      '';
    const domain =
      import.meta.env.VITE_COGNITO_DOMAIN ||
      sessionStorage.getItem(`${cognitoExportPrefix}-domain`) ||
      '';

    if (!userPoolId || !clientId || !domain) {
      throw new Error('Cognito config incomplete');
    }

    return {region, userPoolId, clientId, domain};
  } catch (error) {
    console.error('Failed to load Cognito config:', error);
    throw new Error('Unable to load authentication configuration');
  }
}

export async function getCognitoConfig(): Promise<CognitoConfig> {
  if (cachedConfig) {
    return cachedConfig;
  }

  cachedConfig = await fetchCognitoConfigFromExports();
  return cachedConfig;
}

export async function getCognitoAuthUrl(): Promise<string> {
  const config = await getCognitoConfig();
  const redirectUri = `${window.location.origin}/callback`;
  return `https://${config.domain}.auth.${config.region}.amazoncognito.com/login?client_id=${config.clientId}&response_type=code&scope=openid+email+profile&redirect_uri=${encodeURIComponent(redirectUri)}&identity_provider=MicrosoftEntraID`;
}

export async function refreshIdToken(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  try {
    const config = await getCognitoConfig();
    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: config.clientId,
      refresh_token: refreshToken,
    });

    const response = await fetch(
      `https://${config.domain}.auth.${config.region}.amazoncognito.com/oauth2/token`,
      {
        method: 'POST',
        headers: {'Content-Type': 'application/x-www-form-urlencoded'},
        body: body.toString(),
      }
    );

    if (!response.ok) return null;

    const tokens = await response.json();
    if (tokens.id_token && tokens.access_token) {
      saveTokens(tokens.id_token, tokens.access_token);
      return tokens.id_token;
    }
    return null;
  } catch {
    return null;
  }
}

export async function getCognitoLogoutUrl(): Promise<string> {
  const config = await getCognitoConfig();
  const redirectUri = `${window.location.origin}/logout`;
  return `https://${config.domain}.auth.${config.region}.amazoncognito.com/logout?client_id=${config.clientId}&logout_uri=${encodeURIComponent(redirectUri)}`;
}
